import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import ReactModal from "react-modal";
import { Button, ButtonGroup } from "../ui";
import Separator from "../Separator";
import { Money } from "../formatters";
import Alert from "../Alert";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import FieldGroup from "../FieldGroup";
import { PaymentRow } from "../../orderManagement/atoms";
import { MANUAL_PAYMENT_METHODS } from "../../orderManagement/paymentMethods";
import { XeroOrgSelect } from "../../orderManagement/molecules/XeroOrgSelect";
import { XeroAddressSelect } from "../../orderManagement/molecules/XeroAddressSelect";
import { XeroBrandingSelect } from "../../orderManagement/molecules/XeroBrandingSelect";
import { MarketplaceFeeType } from "../../orderManagement/atoms/MarketplaceFeeType";
import { TaxRate } from "../../orderManagement/molecules";
import { PAYMENT_DIALOG } from "../../orderManagement/constants";
import { useAllFx, tableRate, needsMissingRate } from "../../hooks/fx";
import FxUnavailable from "../formatters/FxUnavailable";
import {
  getBaseCurrency,
  isMulticurrency,
  symbolFor,
} from "../../core/currency";
import { isStagingEnv } from "../../core/config/storeId";

import "../../orderManagement/molecules/payments.scss";

// Fees on these rows are FX / handling / network fees, never a card fee.
const NON_CARD_FEE_METHODS = ["offline", "cash", "usdt"];

// Default new payment rows to the caller-supplied currency (store base first
// — see baseCurrency below) rather than a hardcoded GBP, so a rep on a
// non-GBP store isn't silently converted to GBP.
const NEW_DEPOSIT = (currency = getBaseCurrency()) => ({
  payment_status: "created",
  key: Math.random(),
  amount: 0,
  date: null,
  currency,
  method: "offline",
});

// tableRate reads the row for `from` (USDT reads USD's). A missing rate
// still multiplies by 1 here, but `fxMissing` then hides the totals and
// blocks Save — see FX RULE in hooks/fx.js.
function convertAmount(rates, amount, from = "gbp") {
  return amount * (tableRate(rates, from) || 1);
}

// PRODUCT RULE: the store-base-first default applies to EMPTY, UNTOUCHED rows
// only. Once an admin picks a currency — or a row hydrates from
// `request.deposits` with a currency an admin previously saved — that choice is
// ADMIN-OWNED and the default must never rewrite it. `amount` alone was not a
// sufficient guard: `baseCurrency` re-resolves asynchronously (the Xero-org
// fetch and `listingRate` both land after mount), so a currency picked before
// the amount was typed was silently reverted a moment later.
const currencyIsAdminOwned = (row) =>
  !!row?.currency_touched || !!parseFloat(row?.amount || 0);

// Stamp rows rehydrated from the persisted sell blob as admin-owned when they
// carry a currency, so the guard above holds across a reload.
const markStoredCurrency = (row) =>
  row && row.currency ? { ...row, currency_touched: true } : row;

const StepPayments = ({
  request,
  listing,
  onSave,
  // Deal currency — before any listing/quote resolves one, fall to the
  // store base (evaluated per render; 'gbp' until multi-currency seeds).
  listingRate = getBaseCurrency(),
  price,
  // Multi-org Xero: org whose bank accounts the payment rows load + the picker
  // shown at the top. All optional — only the sell flow passes them.
  xeroConnectionId,
  xeroOrganisations,
  onXeroOrgChange,
  // Which of the SUPPLIER's saved addresses the purchase order is billed to.
  // Sell flow only, like the org picker above.
  supplierId,
  xeroAddressId,
  onXeroAddressChange,
  // Which branding theme (Xero letterhead) the purchase order is generated
  // with. Sell flow only, same as the two above.
  xeroBrandingThemeId,
  onXeroBrandingChange,
  // The PO this money lands on is our own PDF (no Xero on the plan), so no
  // Xero bank account applies: the picker is hidden and not required.
  bankAccountNotNeeded = false,
}) => {
  // Every button in the footer cluster calls the same onSubmit write, so the
  // whole cluster is guarded rather than a couple of labels — otherwise "Save
  // Payments" would remain an open path to the same write from Master Store.
  const { blocked: storeGuardBlocked, reason: storeGuardReason } = useStoreWriteGuard();
  const { rates, status: ratesStatus } = useAllFx(listingRate);

  // Default currency for NEW / unfilled payment rows. Product rule: the
  // STORE BASE currency comes FIRST; the selected Xero org's currency is
  // only the fallback when no base is set (multi-currency off), then the
  // deal currency / GBP before an org is chosen. Filled rows keep their own
  // currency (see effect).
  const baseCurrency = useMemo(() => {
    if (isMulticurrency()) {
      return getBaseCurrency();
    }
    const org = xeroOrganisations?.find(
      (o) => Number(o.id) === Number(xeroConnectionId)
    );
    return (org?.currency || listingRate || "gbp").toLowerCase();
  }, [xeroOrganisations, xeroConnectionId, listingRate]);

  const [totalRemainingPaid, setTotalRemainingPaid] = useState(
    markStoredCurrency(request.deposits?.total_remaining_paid) || {
      amount: 0,
      currency: baseCurrency,
      method: "offline",
    }
  );
  const [taxRate, setTaxRate] = useState(request.deposits?.tax_rate);
  const [deposits, setDeposits] = useState(
    request.deposits?.deposits?.map(markStoredCurrency) || [
      NEW_DEPOSIT(baseCurrency),
    ]
  );
  const [marketplaceFee, setMarketplaceFee] = useState(
    request.deposits?.marketplace_fee || 0
  );
  const [marketplaceFeeType, setMarketplaceFeeType] = useState(
    request.deposits?.marketplace_fee_type || ""
  );
  const [showPaymentErrors, setShowPaymentErrors] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    step: PAYMENT_DIALOG.STEPS.QUESTION,
    decision: null,
  });

  const updateDepositField = (idx, field, value) => {
    setDeposits((deposits) => {
      const listingDeposits = [...(deposits || [])];

      if (
        listingDeposits.length < 3 &&
        idx + 1 === listingDeposits.length &&
        listingDeposits[idx].amount === 0 &&
        field === "amount" &&
        value > 0
      ) {
        listingDeposits.push(NEW_DEPOSIT(baseCurrency));
      }

      if (field !== "amount" || value > 0) {
        listingDeposits[idx] = {
          ...listingDeposits[idx],
          [field]: value,
          // Picking a currency STICKS — see currencyIsAdminOwned.
          ...(field === "currency" ? { currency_touched: true } : {}),
        };
      } else if (
        idx + 1 < listingDeposits.length ||
        listingDeposits.length === 3
      ) {
        if (listingDeposits.length === 3 && listingDeposits[2].amount > 0) {
          listingDeposits.push(NEW_DEPOSIT(baseCurrency));
        }

        listingDeposits.splice(idx, 1);
      }

      return listingDeposits;
    });
  };

  const setTotalRemainingPaidField = (field, value) => {
    setTotalRemainingPaid((totalRemainingPaid) => ({
      ...totalRemainingPaid,
      [field]: value,
      // Picking a currency STICKS — see currencyIsAdminOwned.
      ...(field === "currency" ? { currency_touched: true } : {}),
    }));
  };

  // When the chosen org changes, refresh the default currency on rows the rep
  // hasn't filled yet (amount 0) — new/empty lines follow the org's base
  // currency. Filled rows (and anything saved) keep their own currency so
  // entered or historical amounts are never re-denominated.
  useEffect(() => {
    setDeposits((rows) => {
      let changed = false;
      const next = (rows || []).map((r) => {
        if (!currencyIsAdminOwned(r) && r.currency !== baseCurrency) {
          changed = true;
          return { ...r, currency: baseCurrency };
        }
        return r;
      });
      return changed ? next : rows;
    });
    setTotalRemainingPaid((trp) =>
      !currencyIsAdminOwned(trp) && trp.currency !== baseCurrency
        ? { ...trp, currency: baseCurrency }
        : trp
    );
  }, [baseCurrency]);

  const paidAmount = useMemo(() => {
    let paid = convertAmount(
      rates,
      parseFloat(totalRemainingPaid.amount || 0),
      totalRemainingPaid.currency
    );

    paid += deposits.reduce((sum, deposit) => {
      const depositAmount = convertAmount(
        rates,
        parseFloat(deposit.amount || 0),
        deposit.currency
      );
      return sum + depositAmount;
    }, 0);

    return paid * 100;
  }, [totalRemainingPaid, deposits, rates]);

  const fees = useMemo(() => {
    const cardFees = deposits.reduce((sum, deposit) => {
      if (!NON_CARD_FEE_METHODS.includes(deposit.method)) {
        return (
          sum +
          convertAmount(rates, parseFloat(deposit.fee || 0), deposit.currency)
        );
      }
      return sum;
    }, 0);

    const bankFees = deposits.reduce((sum, deposit) => {
      if (NON_CARD_FEE_METHODS.includes(deposit.method)) {
        return (
          sum +
          convertAmount(rates, parseFloat(deposit.fee || 0), deposit.currency)
        );
      }
      return sum;
    }, 0);

    return { card: cardFees * 100, bank: bankFees * 100 };
  }, [deposits, rates]);

  // FX RULE (hooks/fx.js): true when a payment row is in a currency the rates
  // table has no rate for (request failed and none known, or still loading).
  // The totals below were then computed with a stand-in of 1, so they are
  // shown as "Rate unavailable" and Save refuses — the save status
  // (deposited / done) is decided from them.
  const fxMissing = [totalRemainingPaid, ...(deposits || [])].some(
    (row) =>
      needsMissingRate(rates, listingRate, row?.currency, row?.amount) ||
      needsMissingRate(rates, listingRate, row?.currency, row?.fee)
  );
  const fxUnavailable = fxMissing && ratesStatus !== "loading";

  const dueAmount = price - paidAmount;

  // Staging has no real bank accounts wired up, so requiring one blocks
  // end-to-end testing of the sell/payment flow. Relax the bank-account
  // requirement on STAGING ONLY — production keeps it mandatory.
  const bankAccountRequired = !isStagingEnv() && !bankAccountNotNeeded;

  const arePaymentsValid = useMemo(() => {
    const rowValid = (row) => {
      if (!row || !row.amount || parseFloat(row.amount) <= 0) return true;
      if (!row.date) return false;
      if (bankAccountRequired && !row.bank_account_id) return false;
      return true;
    };

    return deposits.every(rowValid) && rowValid(totalRemainingPaid);
  }, [deposits, totalRemainingPaid, bankAccountRequired]);

  const allPaymentsConfirmed = useMemo(() => {
    const rowConfirmed = (row) => {
      if (!row || !row.amount || parseFloat(row.amount) <= 0) return true;
      return row.payment_status === "completed";
    };
    return deposits.every(rowConfirmed) && rowConfirmed(totalRemainingPaid);
  }, [deposits, totalRemainingPaid]);

  const updatePaymentDialog = (decision) => {
    if (decision === PAYMENT_DIALOG.DECISIONS.CANNOT_DECIDE) {
      setPaymentDialog({
        open: false,
        step: PAYMENT_DIALOG.STEPS.QUESTION,
        decision: null,
      });
      return;
    }

    if (decision === null) {
      setPaymentDialog((d) => ({ ...d, decision: null }));
      return;
    }

    if (decision === PAYMENT_DIALOG.DECISIONS.CONFIRM) {
      setPaymentDialog({
        open: false,
        step: PAYMENT_DIALOG.STEPS.QUESTION,
        decision: null,
      });
      doSave();
      return;
    }

    setPaymentDialog((d) => ({ ...d, decision }));
  };

  const doSave = async () => {
    // The sell flow persists these rows VERBATIM into the `request.deposits`
    // JSON blob (RequestService::addRequestDeposits stores $data as-is), so the
    // row object IS the record: every row must leave here carrying an explicit
    // currency. A legacy row that arrived without one is stamped as legacy GBP
    // — the same value every consumer already reads it as — rather than being
    // re-saved currency-less and re-guessed on the next load.
    const withCurrency = (row) =>
      row ? { ...row, currency: (row.currency || "gbp").toLowerCase() } : row;
    onSave({
      deposits: (deposits || []).map(withCurrency),
      total_remaining_paid: withCurrency(totalRemainingPaid),
      tax_rate: taxRate,
      marketplace_fee: marketplaceFee,
      marketplace_fee_type: marketplaceFeeType,
    });
    if (paidAmount !== 0) {
      const payload = {
        ...request,
        images: request.images?.map((i) => i.id),
        status: paidAmount < price ? "deposited" : "done",
      };
      await axios.put(`/api/requests/${request.id}`, payload);
    }
  };

  const onSubmit = () => {
    if (!arePaymentsValid) {
      setShowPaymentErrors(true);
      return;
    }
    if (fxMissing) {
      setShowPaymentErrors(true);
      return;
    }
    // A deposit is meant to be partial (paid < listing price), so saving one
    // should NOT raise the "Total Paid ≠ Listing Price" confirmation — that only
    // makes sense for an OVERPAYMENT (paid > price), which is genuinely
    // suspicious. Partial deposits save straight through (doSave stamps status
    // "deposited"); exact payment saves as "done".
    if (paidAmount && paidAmount > price) {
      setPaymentDialog({
        open: true,
        step: PAYMENT_DIALOG.STEPS.QUESTION,
        decision: null,
      });
      return;
    }
    doSave();
  };

  return (
    <>
      <div className="order-payments order-management-flow mt-4">
        <div className="xero-doc-selects">
          {/* Xero organisation picker — rendered inside this same container as the
              DEPOSIT PAID fields so it lines up with them. Only the sell flow
              passes orgs; other StepPayments callers omit them and it stays hidden. */}
          <XeroOrgSelect
            organisations={xeroOrganisations}
            value={xeroConnectionId}
            onChange={onXeroOrgChange}
          />

          {/* The branding theme (letterhead) the PURCHASE ORDER is generated
              with, beside the org whose themes these are. Only rendered when the
              caller wires it up — other StepPayments callers omit it. */}
          {onXeroBrandingChange && (
            <XeroBrandingSelect
              xeroConnectionId={xeroConnectionId}
              value={xeroBrandingThemeId}
              onChange={onXeroBrandingChange}
            />
          )}
        </div>

        {/* Which of the supplier's saved addresses the purchase order is billed
            to. Pushed onto their Xero contact when the PO is generated, so it
            appears on the PDF. Only rendered when the caller supplies a
            supplier — other StepPayments callers omit it and it stays hidden. */}
        {supplierId && onXeroAddressChange && (
          <XeroAddressSelect
            userId={supplierId}
            value={xeroAddressId}
            onChange={onXeroAddressChange}
          />
        )}

        {listing && (
          <div className="title mt-5 mb-4">
            Sell Request : {listing.product?.brand?.name}{" "}
            {listing.product?.model?.name} |&nbsp;
            {listing.product?.reference_number} |&nbsp;
            <Money amount={price} className="title" currency={listingRate} />
          </div>
        )}

        <div className="strong-label mb-4">Deposit Paid:</div>

        {deposits?.map((deposit, idx) => (
          <PaymentRow
            key={deposit.key}
            idx={idx}
            // /sell flow: money paid TO the seller after we acquire an
            // item. Stripe Link is for collecting from customers — doesn't
            // apply here. Hide the pill so the rep can't pick a dead-end
            // method.
            allowedMethods={MANUAL_PAYMENT_METHODS}
            hideBankAccount={bankAccountNotNeeded}
            xeroConnectionId={xeroConnectionId}
            paymentStatus={deposit.payment_status}
            setPaymentStatus={(value) =>
              updateDepositField(idx, "payment_status", value)
            }
            amount={deposit.amount}
            setAmount={(value) => updateDepositField(idx, "amount", value)}
            currency={deposit.currency}
            setCurrency={(value) => updateDepositField(idx, "currency", value)}
            date={deposit.date}
            setDate={(value) => updateDepositField(idx, "date", value)}
            method={deposit.method}
            setMethod={(value) => updateDepositField(idx, "method", value)}
            cardType={deposit.card_type}
            setCardType={(value) =>
              updateDepositField(idx, "card_type", value)
            }
            fee={deposit.fee}
            setFee={(value) => updateDepositField(idx, "fee", value)}
            bankAccountId={deposit.bank_account_id}
            setBankAccountId={(value) =>
              updateDepositField(idx, "bank_account_id", value)
            }
            showErrors={showPaymentErrors}
          />
        ))}

        <TaxRate taxRate={taxRate} setTaxRate={setTaxRate} />

        <Separator />

        <div className="strong-label mt-5 mb-4">Total Remaining Paid</div>

        <PaymentRow
          // /sell flow TRP — same reasoning as deposit rows above: Stripe
          // Link doesn't apply to money paid TO the seller.
          allowedMethods={MANUAL_PAYMENT_METHODS}
          hideBankAccount={bankAccountNotNeeded}
          xeroConnectionId={xeroConnectionId}
          paymentStatus={totalRemainingPaid.payment_status}
          setPaymentStatus={(value) =>
            setTotalRemainingPaidField("payment_status", value)
          }
          amount={totalRemainingPaid.amount}
          setAmount={(value) => setTotalRemainingPaidField("amount", value)}
          currency={totalRemainingPaid.currency}
          setCurrency={(value) =>
            setTotalRemainingPaidField("currency", value)
          }
          date={totalRemainingPaid.date}
          setDate={(value) => setTotalRemainingPaidField("date", value)}
          method={totalRemainingPaid.method}
          setMethod={(value) => setTotalRemainingPaidField("method", value)}
          cardType={totalRemainingPaid.card_type}
          setCardType={(value) =>
            setTotalRemainingPaidField("card_type", value)
          }
          bankAccountId={totalRemainingPaid.bank_account_id}
          setBankAccountId={(value) =>
            setTotalRemainingPaidField("bank_account_id", value)
          }
          fee={totalRemainingPaid.fee}
          setFee={(value) => setTotalRemainingPaidField("fee", value)}
          showErrors={showPaymentErrors}
        />

        <div className="strong-label mt-5 mb-4">Marketplace Fee:</div>

        {/*
          Two-column row that matches Deposit/TRP rows above so the
          rep can read the Payments form as one consistent grid:
          column 1 (100px) ↔ currency dropdown, column 2 (120px)
          ↔ amount field. The previous 110px inline spacer was added
          when V2 wasn't a thing; V2's dark-theme `.currency` is
          100px so the row was offset to the right of TRP's columns.
        */}
        <div className="d-flex mb-3 marketplace-fee-row">
          <div className="marketplace-fee-row__type-col">
            <div className="card-type-container bs-5">
              <MarketplaceFeeType
                value={marketplaceFeeType}
                onChange={setMarketplaceFeeType}
                readOnly={false}
              />
            </div>
          </div>
          <div className="marketplace-fee-row__amount-col amount">
            <div className="marketplace-fee-field">
              <FieldGroup
                type="numberFormat"
                name="marketplace_fee"
                persistLabel
                inputStyle={{
                  paddingLeft: marketplaceFee ? "30px" : "10px",
                  width: "120px",
                  paddingTop: "4px",
                }}
                // Marketplace fee is entered in the deal currency — the
                // symbol hint must follow it, not a hardcoded £.
                placeholder={marketplaceFee ? symbolFor(listingRate) : ""}
                noFormik
                value={marketplaceFee}
                onChange={setMarketplaceFee}
              />
            </div>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-12 col-lg-8">
            <div className="label-value-row">
              <div className="strong-label">Total Listing Price:</div>
              <div className="value">
                <Money amount={price} currency={listingRate} />
              </div>
            </div>

            <div className="label-value-row">
              <div className="strong-label">Total Paid By Client:</div>
              <div className="value">
                {fxMissing && <FxUnavailable unavailable={fxUnavailable} />}
                {!fxMissing && <Money amount={paidAmount} zero currency={listingRate} />}
                {!fxMissing && !!paidAmount && paidAmount !== price && (
                  <>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; (Due:{" "}
                    <Money amount={dueAmount} zero currency={listingRate} />)
                  </>
                )}
              </div>
            </div>

            <div className="label-value-row">
              <div className="strong-label">Total Fees:</div>
              <div className="value">
                {fxMissing ? (
                  <FxUnavailable unavailable={fxUnavailable} />
                ) : (
                  <Money
                    amount={-fees.card - fees.bank}
                    zero
                    currency={listingRate}
                  />
                )}
              </div>
            </div>

            <div className="label-value-row">
              <div className="strong-label">Total Paid After Fees:</div>
              <div className="value">
                {fxMissing ? (
                  <FxUnavailable unavailable={fxUnavailable} />
                ) : (
                  <Money
                    amount={paidAmount - fees.card - fees.bank}
                    zero
                    currency={listingRate}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="error-line mt-3">
          {showPaymentErrors && !arePaymentsValid && (
            <>Not all required fields are filled in.</>
          )}
          {showPaymentErrors && arePaymentsValid && fxMissing && (
            <>
              Exchange rate unavailable, so the payments can't be totalled.
              Nothing was saved. Try again shortly.
            </>
          )}
        </div>

        <div className="action-buttons text-right payment-buttons mt-2">
          {/*
            type="button" (the Button default) is required: this renders inside
            RequestTemplate's <form>; type="submit" would reload the page before
            onSave() and afterAddingPrice() advance the flow to PURCHASE
            ORDER. Click handler stays on the button (not the wrapper) so
            stray clicks in row gutters don't fire it.
          */}
          {!paidAmount && (
            <Button variant="primary" data-btn="payment-save-deposits" onClick={onSubmit} disabled={storeGuardBlocked} title={storeGuardReason}>
              Save deposits
            </Button>
          )}
          {!!dueAmount && !!paidAmount && !totalRemainingPaid.amount && (
            <Button variant="primary" data-btn="payment-save-deposits" onClick={onSubmit} disabled={storeGuardBlocked} title={storeGuardReason}>
              Save deposits
            </Button>
          )}
          {!!dueAmount && !!paidAmount && !!totalRemainingPaid.amount && (
            <Button variant="primary" data-btn="payment-save-review" onClick={onSubmit} disabled={storeGuardBlocked} title={storeGuardReason}>
              Review payments
            </Button>
          )}
          {!dueAmount &&
            !!paidAmount &&
            !!totalRemainingPaid.amount &&
            !allPaymentsConfirmed && (
              <Button variant="primary" data-btn="payment-save" onClick={onSubmit} disabled={storeGuardBlocked} title={storeGuardReason}>
                Save payments
              </Button>
            )}
          {!dueAmount &&
            !!paidAmount &&
            !!totalRemainingPaid.amount &&
            allPaymentsConfirmed && (
              <Button variant="primary" data-btn="payment-confirm-paid" onClick={onSubmit} disabled={storeGuardBlocked} title={storeGuardReason}>
                Mark as paid
              </Button>
            )}
        </div>
      </div>

      <ReactModal
        {...{
          isOpen: paymentDialog.open,
          onRequestClose: () =>
            updatePaymentDialog(PAYMENT_DIALOG.DECISIONS.CANNOT_DECIDE),
          portalClassName: "payment-dialog-portal",
          style: {
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              borderRadius: "5px",
              border: "none",
            },
          },
        }}
      >
        <div className="payment-dialog">
          <Alert
            type="warning"
            message={
              paymentDialog.step === PAYMENT_DIALOG.STEPS.QUESTION
                ? "Action needed"
                : "Confirmation needed"
            }
          >
            <p>
              {!paymentDialog.decision && paidAmount !== price && (
                <>
                  {/* Both figures are in the deal currency (listingRate). */}
                  Total Paid: <Money amount={paidAmount} currency={listingRate} />{" "}
                  is not equal to Listing Price:{" "}
                  <Money amount={price} currency={listingRate} />.
                </>
              )}
            </p>
          </Alert>
          <ButtonGroup align="end" className="action-buttons mt-4">
            {!paymentDialog.decision ? (
              <Button
                variant="secondary"
                data-btn="payment-cancel-dialog"
                onClick={() =>
                  updatePaymentDialog(PAYMENT_DIALOG.DECISIONS.CANNOT_DECIDE)
                }
              >
                I will fix payment records
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  data-btn="payment-cancel-back"
                  onClick={() => updatePaymentDialog(null)}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  data-btn="payment-confirm-dialog"
                  onClick={() =>
                    updatePaymentDialog(PAYMENT_DIALOG.DECISIONS.CONFIRM)
                  }
                >
                  Confirm
                </Button>
              </>
            )}
          </ButtonGroup>
        </div>
      </ReactModal>
    </>
  );
};

export default StepPayments;
